const EXCLUDED_KEYS = [
  "metadata",
  "children",
  "summary",
  "group",
  "level",
  "id",
  "name",
];

const DEFAULT_KEYS = [
  "label",
  "code",
  "debit_to_bcy",
  "credit_to_bcy",
  "opening_balance_to_bcy",
  "running_balance_to_bcy",
];

const toReadableHeader = (key) =>
  key
    .replace(/_to_bcy/g, "")
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const discoverReportKeys = (data) => {
  const searchRow = (rows = []) => {
    for (const row of rows) {
      if (row.code && row.name) return row;
      const nested = searchRow(row.children);
      if (nested) return nested;
    }
    return null;
  };

  const firstAccountRow = searchRow(data?.rows);

  if (!firstAccountRow) {
    return DEFAULT_KEYS.map((key) => ({
      key,
      header: toReadableHeader(key),
      align: key.endsWith("_to_bcy") ? "right" : "left",
    }));
  }

  return Object.keys(firstAccountRow)
    .filter((key) => !EXCLUDED_KEYS.includes(key))
    .sort((left, right) => {
      if (left === "label") return -1;
      if (right === "label") return 1;
      if (left === "code") return -1;
      if (right === "code") return 1;
      return left.localeCompare(right);
    })
    .map((key) => ({
      key,
      header: toReadableHeader(key) || key,
      align: key.endsWith("_to_bcy") ? "right" : "left",
    }));
};

const formatCellValue = (value) => {
  if (typeof value !== "number") return value ?? "-";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const flattenRows = (rows = [], level = 0) =>
  rows.flatMap((row) => {
    const current = { ...row, level };
    const children = row.children ? flattenRows(row.children, level + 1) : [];
    return [current, ...children];
  });

export const exportTrialBalanceToCSV = (data, filters, toast) => {
  if (!data?.rows) {
    toast({ title: "No data to export", status: "warning", duration: 3000 });
    return;
  }

  const columns = discoverReportKeys(data);
  let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
  csvContent += `${columns.map((column) => column.header).join(",")}\r\n`;

  flattenRows(data.rows).forEach((row) => {
    const isSummary = row.summary && (row.summary.sub_totals || row.summary.totals);
    if (!(row.code && row.name) && !(row.group && row.id === row.group) && !isSummary) {
      return;
    }

    const source = isSummary ? row.summary : row;
    const rowData = columns.map((column) => {
      const value =
        column.key === "label"
          ? source.label || row.name || ""
          : source[column.key] ?? row[column.key] ?? "";
      return `"${formatCellValue(value)}"`;
    });
    csvContent += `${rowData.join(",")}\r\n`;
  });

  const link = document.createElement("a");
  link.href = encodeURI(csvContent);
  link.download = `Trial_Balance_${filters.from_date}_to_${filters.to_date}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  toast({ title: "CSV exported successfully", status: "success", duration: 3000 });
};

const generatePDFRows = (rows, columns, level = 0) =>
  rows
    .map((row) => {
      const isGroupHeader = row.group && row.id === row.group && !row.code;
      const isAccountRow = row.code && row.name;
      const isSummary = row.summary && (row.summary.sub_totals || row.summary.totals);
      const source = isSummary ? row.summary : row;
      const children = row.children ? generatePDFRows(row.children, columns, level + 1) : "";

      if (!isGroupHeader && !isAccountRow && !isSummary) {
        return children;
      }

      const rowClass = isSummary ? "total-row" : isGroupHeader ? "group-row" : "data-row";
      const cells = columns
        .map((column) => {
          const value =
            column.key === "label"
              ? source.label || row.name || ""
              : source[column.key] ?? row[column.key] ?? "";
          const content =
            column.key === "label"
              ? `<span style="margin-left:${level * 16}px">${formatCellValue(value)}</span>`
              : formatCellValue(value);
          return `<td class="${column.align === "right" ? "text-right" : "text-left"}">${content}</td>`;
        })
        .join("");

      return `<tr class="${rowClass}">${cells}</tr>${children}`;
    })
    .join("");

export const exportTrialBalanceToPDF = (data, filters, toast) => {
  if (!data?.rows) {
    toast({ title: "No data to export", status: "warning", duration: 3000 });
    return;
  }

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    toast({
      title: "Print error",
      description: "Popup window was blocked. Please allow popups for this site.",
      status: "error",
      duration: 5000,
    });
    return;
  }

  const columns = discoverReportKeys(data);
  printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8" />
    <title>Trial Balance Report</title>
    <style>
      body{font-family:Arial,sans-serif;margin:20px;color:#333}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #ddd;padding:8px 12px}
      th{background:#2c5aa0;color:#fff;text-align:center}
      .group-row{background:#e8f4fd;font-weight:700}.total-row{background:#d4edda;font-weight:700}
      .text-right{text-align:right}.text-left{text-align:left}
    </style></head><body>
    <h2>Trial Balance Report</h2><p>Period: ${filters.from_date} to ${filters.to_date}</p>
    <table><thead><tr>${columns.map((column) => `<th>${column.header}</th>`).join("")}</tr></thead>
    <tbody>${generatePDFRows(data.rows, columns)}</tbody></table>
    <script>window.onload=function(){setTimeout(()=>window.print(),500);}</script>
    </body></html>`);
  printWindow.document.close();

  toast({
    title: "Opening print window",
    description: "The print window will open automatically",
    status: "info",
    duration: 3000,
  });
};
